import { useEffect, useMemo, useState } from 'react';
import { formatPkr } from '@raahi/shared';
import type { TripOffer } from '../state/offer.js';
import { VehicleIllustration } from './VehicleIllustration.js';

interface Props {
  offer: TripOffer;
  onAccept: () => void;
  onDecline: () => void;
  busy?: boolean;
}

const OFFER_WINDOW_MS = 15_000;

export function OfferCard({ offer, onAccept, onDecline, busy }: Props) {
  const expiresAt = useMemo(() => new Date(offer.expiresAt).getTime(), [offer.expiresAt]);
  const startedAt = expiresAt - OFFER_WINDOW_MS;
  const [now, setNow] = useState(() => Date.now());

  useEffect(() => {
    const id = setInterval(() => setNow(Date.now()), 100);
    return () => clearInterval(id);
  }, []);

  const remainingMs = Math.max(0, expiresAt - now);
  const remaining = Math.ceil(remainingMs / 1000);
  const progress = Math.max(0, Math.min(1, (now - startedAt) / OFFER_WINDOW_MS));

  return (
    <div className="card overflow-hidden p-4">
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-2">
          <span className="chip border-alert/40 bg-alert/10 text-alert">New request</span>
          <VehicleIllustration type="economy" size={28} />
        </div>
        <span className={'num text-sm font-semibold ' + (remaining <= 5 ? 'text-red-soft' : 'text-alert')}>
          {remaining}s
        </span>
      </div>

      <div className="mt-3 h-1.5 w-full overflow-hidden rounded-full bg-elevated">
        <div
          className={'h-full transition-[width] duration-100 ' + (remaining <= 5 ? 'bg-red-soft' : 'bg-alert')}
          style={{ width: `${(1 - progress) * 100}%` }}
        />
      </div>

      <div className="mt-3 grid grid-cols-2 gap-3">
        <div>
          <p className="text-xs uppercase tracking-wide text-muted">Distance</p>
          <p className="num font-display text-2xl font-semibold">{offer.distanceKm.toFixed(1)} km</p>
        </div>
        <div className="text-right">
          <p className="text-xs uppercase tracking-wide text-muted">Estimated fare</p>
          <p className="num font-display text-2xl font-semibold">{formatPkr(offer.estimatedFare)}</p>
        </div>
      </div>

      <div className="mt-4 grid grid-cols-2 gap-2">
        <button type="button" className="btn-ghost" onClick={onDecline} disabled={busy}>
          Decline
        </button>
        <button
          type="button"
          className="btn-go"
          onClick={onAccept}
          disabled={busy || remaining === 0}
        >
          {busy ? 'Accepting…' : 'Accept'}
        </button>
      </div>
    </div>
  );
}
